import { jsPDF } from "jspdf";

/**
 * Entry point
 */
export async function generateImagePDF({ files, count = 1, size = "passport" }) {
    count = count >= 28 ? 28 : count;

    if (!files || files.length === 0) {
        throw new Error("No files provided");
    }

    if (size === "3R") return generateFixedSize(files, "3R");
    if (size === "4R") return generateFixedSize(files, "4R");
    if (size === "6R") return generateFixedSize(files, "6R");
    if (size === "landscape") return generateLandscape(files, count);
    if (size === "ngo_combined") return generateNGOCombinedPDF(files, count);

    return generatePortrait(files, count, size);
}

/* ---------------------------------------------------
   CONFIG
--------------------------------------------------- */

function getPortraitConfig(size) {
    const map = {
        passport: { w: 38, h: 50, margin: 3 },
        stamp: { w: 20, h: 25, margin: 3 },
        "2x4": { w: 50, h: 76, margin: 1.5 },
        "2x2": { w: 50, h: 50, margin: 1 },
        nid: { w: 85.6, h: 54, margin: 3, x: 62.2, y: 50.8, single: true },
        nid_combined: { w: 90, h: 115, margin: 3, x: 60, y: 50.8, single: true },
        bordered: { w: 184.6, h: 271.6, margin: 3, x: 12.7, y: 12.7, single: true },
        mrp: { w: 125, h: 176, margin: 3, x: 42.5, y: 50.8, single: true },
    };

    return map[size] || map.passport;
}

/* ---------------------------------------------------
   PORTRAIT PDF
--------------------------------------------------- */

async function generatePortrait(files, count, size) {
    const pdf = new jsPDF("p", "mm", "a4");
    const cfg = getPortraitConfig(size);

    let x = cfg.x ?? 3;
    let y = cfg.y ?? 3;
    const startX = x;
    const startY = y;

    const maxW = pdf.internal.pageSize.width;
    const maxH = pdf.internal.pageSize.height;

    for (const file of files) {
        const img = await loadImage(file);

        for (let i = 0; i < count; i++) {
            pdf.addImage(img, "JPEG", x, y, cfg.w, cfg.h);

            if (!cfg.single) {
                pdf.rect(x, y, cfg.w, cfg.h);
            }

            x += cfg.w + cfg.margin;

            if (x + cfg.w > maxW) {
                x = startX;
                y += cfg.h + cfg.margin;
            }

            if (y + cfg.h > maxH) {
                pdf.addPage();
                x = startX;
                y = startY;
            }

            if (cfg.single) break;
        }
    }

    pdf.save("images.pdf");
}

/* ---------------------------------------------------
   LANDSCAPE PDF
--------------------------------------------------- */

async function generateLandscape(files, count) {
    const pdf = new jsPDF("l", "mm", "a4");

    const imgW = 38;
    const imgH = 49;
    const margin = 2.5;

    let x = 3;
    let y = 3;

    const maxW = pdf.internal.pageSize.width;
    const maxH = pdf.internal.pageSize.height;

    for (const file of files) {
        const img = await loadImage(file);

        for (let i = 0; i < count; i++) {
            pdf.addImage(img, "JPEG", x, y, imgW, imgH);
            pdf.rect(x, y, imgW, imgH);

            y += imgH + margin;

            if (y + imgH > maxH) {
                x += imgW + margin;
                y = 3;

                if (x + imgW > maxW) {
                    pdf.addPage();
                    x = 3;
                    y = 3;
                }
            }
        }
    }

    pdf.save("images.pdf");
}

/* ---------------------------------------------------
   FIXED PHOTO SIZES (3R, 4R, 6R)
--------------------------------------------------- */

const FIXED_SIZES = {
    "3R": { w: 89, h: 127, margin: 6 },
    "4R": { w: 99, h: 140, margin: 4 },
    "6R": { w: 202, h: 142, margin: 6 },
};

async function generateFixedSize(files, type) {
    const pdf = new jsPDF("p", "mm", "a4");
    const cfg = FIXED_SIZES[type];

    let x = 3;
    let y = 3;

    const maxW = pdf.internal.pageSize.width;
    const maxH = pdf.internal.pageSize.height;

    for (const file of files) {
        const img = await loadImage(file);

        pdf.addImage(img, "JPEG", x, y, cfg.w, cfg.h);

        x += cfg.w + cfg.margin;

        if (x + cfg.w > maxW) {
            x = 3;
            y += cfg.h + cfg.margin;
        }

        if (y + cfg.h > maxH) {
            pdf.addPage();
            x = 3;
            y = 3;
        }
    }

    pdf.save("images.pdf");
}

/* ---------------------------------------------------
   HELPERS
--------------------------------------------------- */

function loadImage(file) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = e => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
}

/* ---------------------------------------------------
   Combine 2 image for NGO combine
--------------------------------------------------- */
// async function combineTwoImages(img1, img2, direction = "vertical") {
//     const canvas = document.createElement("canvas");
//     const ctx = canvas.getContext("2d");

//     if (direction === "vertical") {
//         canvas.width = Math.max(img1.width, img2.width);
//         canvas.height = img1.height + img2.height;

//         ctx.drawImage(img1, 0, 0);
//         ctx.drawImage(img2, 0, img1.height);
//     } else {
//         canvas.width = img1.width + img2.width;
//         canvas.height = Math.max(img1.height, img2.height);

//         ctx.drawImage(img1, 0, 0);
//         ctx.drawImage(img2, img1.width, 0);
//     }

//     return canvas.toDataURL("image/jpeg", 1.0);
// }

async function combineTwoImagesSideBySide(
    img1,
    img2,
    {
        width = 760,   // total canvas width (px)
        height = 490,  // canvas height (px)
        gap = 0        // space between images
    } = {}
) {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = width;
    canvas.height = height;

    const halfWidth = (width - gap) / 2;

    // left image
    drawCover(ctx, img1, 0, 0, halfWidth, height);

    // right image
    drawCover(ctx, img2, halfWidth + gap, 0, halfWidth, height);

    return canvas.toDataURL("image/jpeg", 1.0);
}

function drawCover(ctx, img, x, y, w, h) {
    const imgRatio = img.width / img.height;
    const boxRatio = w / h;

    let drawWidth, drawHeight;

    if (imgRatio > boxRatio) {
        drawHeight = h;
        drawWidth = h * imgRatio;
    } else {
        drawWidth = w;
        drawHeight = w / imgRatio;
    }

    const offsetX = x + (w - drawWidth) / 2;
    const offsetY = y + (h - drawHeight) / 2;

    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
}


/* ---------------------------------------------------
   Generate NGO Combined PDF
--------------------------------------------------- */

function base64ToFile(base64, filename = "combined.jpg") {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
}

// function rotateBase64Image90(base64) {
//     return new Promise((resolve) => {
//         const img = new Image();
//         img.onload = () => {
//             const canvas = document.createElement("canvas");
//             const ctx = canvas.getContext("2d");

//             // Swap width and height for 90° rotation
//             canvas.width = img.height;
//             canvas.height = img.width;

//             // Rotate 90 degrees clockwise
//             ctx.translate(canvas.width, 0);
//             ctx.rotate((-90 * Math.PI) / 180);

//             // Draw original image
//             ctx.drawImage(img, 0, 0);

//             resolve(canvas.toDataURL("image/jpeg", 1.0));
//         };
//         img.src = base64;
//     });
// }

function rotateBase64Image90CounterClockwise(base64) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            // Swap width and height
            canvas.width = img.height;
            canvas.height = img.width;

            // Rotate 90° counterclockwise
            ctx.translate(0, canvas.height);
            ctx.rotate((-90 * Math.PI) / 180);

            // Draw original image
            ctx.drawImage(img, 0, 0);

            resolve(canvas.toDataURL("image/jpeg", 1.0));
        };
        img.src = base64;
    });
}

async function prepareNGOCombinedFiles(files) {
    const combinedFiles = [];

    for (let i = 0; i < files.length; i += 2) {
        const img1 = await loadImage(files[i]);
        const img2 = files[i + 1]
            ? await loadImage(files[i + 1])
            : null;

        let combinedBase64;

        if (img2) {
            combinedBase64 = await combineTwoImagesSideBySide(
                img1,
                img2,
                { width: 490, height: 380 }
            );
        } else {
            const canvas = document.createElement("canvas");
            canvas.width = img1.width;
            canvas.height = img1.height;
            canvas.getContext("2d").drawImage(img1, 0, 0);
            combinedBase64 = canvas.toDataURL("image/jpeg", 1.0);
        }

        // // Rotate 90 degrees
        // combinedBase64 = await rotateBase64Image90(combinedBase64);
        // Rotate 90 degrees counterclockwisw
        combinedBase64 = await rotateBase64Image90CounterClockwise(combinedBase64);

        combinedFiles.push(
            base64ToFile(combinedBase64, `ngo_combined_${i}.jpg`)
        );
    }

    return combinedFiles;
}

async function generateNGOCombinedPDF(files) {

    const combinedFiles = await prepareNGOCombinedFiles(files);
    generateLandscape(
        combinedFiles,
        4, 
        "landscape"
    );
}
